import { client } from "./supabase";

const ONSIGN_PRECIO_POR_PANTALLA = 10.99;

const contarPantallasActivas = async (pais, fecha) => {
  const [edificiosRes, carteleriaRes] = await Promise.all([
    client
      .from("edificios")
      .select("pantallas, cowork, espera, totems, inicio_servicio")
      .eq("pais", pais)
      .eq("estado", "OPERATIVO")
      .lte("inicio_servicio", fecha),
    client
      .from("negocios_carteleria")
      .select("cantidad_pantallas, fecha_inicio, plazo_contrato_meses")
      .eq("pais", pais)
      .eq("eliminada", false)
      .or("estado.eq.Activo,estado.eq.activo,estado.eq.100%")
      .lte("fecha_inicio", fecha),
  ]);

  let total = 0;

  for (const e of edificiosRes.data || []) {
    total += (Number(e.pantallas) || 0) + (Number(e.cowork) || 0) + (Number(e.espera) || 0) + (Number(e.totems) || 0);
  }

  for (const n of carteleriaRes.data || []) {
    if (!n.cantidad_pantallas) continue;
    if (n.plazo_contrato_meses && n.fecha_inicio) {
      const fechaFin = new Date(n.fecha_inicio);
      fechaFin.setMonth(fechaFin.getMonth() + parseInt(n.plazo_contrato_meses, 10));
      const fechaFinStr = fechaFin.toISOString().split("T")[0];
      if (fechaFinStr < fecha) continue;
    }
    total += Number(n.cantidad_pantallas);
  }

  return total;
};

export const getProveedoresRecurrentesEntries = async (pais, monedaLocal = "CLP", facturasRecibidas = []) => {
  const { data: proveedores, error } = await client
    .from("proveedores")
    .select("id, nombre, alias, monto_recurrente, moneda_recurrente, dia_pago, frecuencia")
    .eq("pais", pais)
    .eq("habilitado", true)
    .eq("es_recurrente", true);

  if (error) throw error;

  // Set de `${proveedor_id}-YYYY-MM` que ya tienen factura SII vinculada en ese mes calendario.
  // Si existe, no generamos entry sintética para evitar doble conteo con la fila de "Por pagar".
  // Comparación SIEMPRE por proveedor_id numérico.
  const mesProveedorConFactura = new Set();
  for (const f of facturasRecibidas) {
    if (!f.proveedor_id || !f.fecha_emision) continue;
    mesProveedorConFactura.add(`${f.proveedor_id}-${f.fecha_emision.slice(0, 7)}`);
  }

  const hoy = new Date().toISOString().split("T")[0];
  const hoyDate = new Date();
  const finAnio = new Date(hoyDate.getFullYear(), 11, 31);
  const entries = [];

  const onsignProv = (proveedores || []).find((p) => p.nombre.toLowerCase().includes("onsign"));

  const meses = [];
  const cursor = new Date(hoyDate.getFullYear(), hoyDate.getMonth(), 1);
  while (cursor <= finAnio) {
    meses.push(new Date(cursor));
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const pantallasXMes = {};
  if (onsignProv) {
    for (const mes of meses) {
      const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
      const fechaStr = ultimoDia.toISOString().split("T")[0];
      // eslint-disable-next-line no-await-in-loop
      pantallasXMes[fechaStr] = await contarPantallasActivas(pais, fechaStr);
    }
  }

  for (const prov of proveedores || []) {
    const esOnSign = prov.nombre.toLowerCase().includes("onsign");

    for (const mes of meses) {
      const diaPago = prov.dia_pago || 30;
      const fechaPagoDate = new Date(mes.getFullYear(), mes.getMonth(), diaPago);

      if (fechaPagoDate.getMonth() !== mes.getMonth()) {
        fechaPagoDate.setDate(0);
      }

      const fechaPago = fechaPagoDate.toISOString().split("T")[0];
      if (fechaPago <= hoy) continue;

      const mesKey = `${mes.getFullYear()}-${String(mes.getMonth() + 1).padStart(2, "0")}`;
      if (mesProveedorConFactura.has(`${prov.id}-${mesKey}`)) continue;

      let monto;

      if (esOnSign) {
        const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).toISOString().split("T")[0];
        const pantallas = pantallasXMes[ultimoDia] || 0;
        monto = Math.round(pantallas * ONSIGN_PRECIO_POR_PANTALLA * 100) / 100;
      } else {
        monto = Number(prov.monto_recurrente || 0);
      }

      if (monto <= 0) continue;

      const ultimoDiaKey = new Date(mes.getFullYear(), mes.getMonth() + 1, 0).toISOString().split("T")[0];

      entries.push({
        id: `recurrente-${prov.id}-${fechaPago}`,
        fecha_display: fechaPago,
        importeEnMoneda: -monto,
        moneda_recurrente: prov.moneda_recurrente || monedaLocal,
        razon_social_receptor: esOnSign
          ? `${prov.alias || prov.nombre} (${pantallasXMes[ultimoDiaKey] || 0} licencias)`
          : prov.alias || prov.nombre,
        tipo: "proveedor",
        es_recurrente: true,
      });
    }
  }

  return entries.sort((a, b) => a.fecha_display.localeCompare(b.fecha_display));
};
